import { IconButton, Spinner, Icon, useDisclosure } from '@chakra-ui/react';
import { useHomesStore, useUIStore } from '~/store';
import { MdWork, MdMessage } from 'react-icons/md';
import { SendMessageModal } from '~/components/buyers/admin/SendMessageModal';
import { useSpeculatorSelectionStore } from '~/store/useBuyerMatchSelectionStore';
import { useUserAction } from '~/hooks/useUserAction';
import { useRouter } from 'next/navigation';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useUser } from '~/hooks/useUser';
import type { CategoryType, NestedAddress } from '@habity/types';
import { getAPI } from '~/lib/api';
import { ROUTES } from '@habity/constants';
import { useBuyerDetails } from '~/hooks/useBuyerDetails';
import { useFilters } from '~/hooks/useFilters';
import { ActionButtons } from './ActionButtons';
import { isSearchRoute } from '@habity/utils';
import {
  getCategoryText,
  getSearchPath,
  getSellPath,
  getTabNameFromUrlPath,
} from '@habity/services';
import { FiList } from 'react-icons/fi';
import { VscSettings } from 'react-icons/vsc';
import { useSearchParams, usePathname } from 'next/navigation';
import { ICONS } from '~/constants';
import { isAdmin, isBroker } from '~/lib/roles';

import { usePathUtils } from '~/hooks/usePathUtils';
import { useSearchActionRequirements } from '~/hooks/useSearchActionRequirements';
import {
  getDefaultMessageTitle,
  getDefaultMessageBody,
  getPersonalMessageBody,
  toPropertyDetails,
} from '~/lib/messages/speculatorMessageHelpers';
import { getAddressDetailsSentenceForEmail } from '@habity/services';

export const ListButtons = () => {
  const { filters, hasFilters } = useFilters();
  const { canExecute: canCreateSearch } = useSearchActionRequirements();
  const count = useHomesStore((state) => state.count);
  // We only need isLoading for the UI state, not setting it
  const [isLoading] = useState<boolean>(false);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const searchId = searchParams?.get('searchId');
  const label = searchParams?.get('label');
  const map = searchParams?.get('map');
  const homeIdFromUrl = searchParams?.get('homeId');
  const tab = getTabNameFromUrlPath(pathname);
  const { replace } = useRouter();
  const { isExactPath } = usePathUtils();

  const { user } = useUser();
  const { executeAction, isLoading: isExecutingAction } = useUserAction();
  const setModals = useUIStore((state) => state.setModals);
  const modals = useUIStore((state) => state.modals);
  const lastCreatedSearchId = useUIStore((state) => state.lastCreatedSearchId);
  const setLastCreatedSearchId = useUIStore(
    (state) => state.setLastCreatedSearchId
  );
  const setEditSearchId = useUIStore((state) => state.setEditSearchId);
  const { showSpeculatorDetails, toggleSpeculatorDetails } = useBuyerDetails();

  // Send message modal for speculators
  const sendMessageModal = useDisclosure();
  const { speculatorsSelection: buyersSelectionToMatch } =
    useSpeculatorSelectionStore();

  // Extract address ID from label (format: "address:id:Street, City")
  const addressId = useMemo(() => {
    if (!label?.startsWith('address:')) return null;
    const parts = label.split(':');
    if (parts.length >= 2 && parts[1] !== '0') {
      return parts[1];
    }
    return null;
  }, [label]);

  // Clear lastCreatedSearchId when search params change (user is modifying their search)
  const searchParamsString = searchParams?.toString() ?? '';
  const prevSearchParamsRef = useRef(searchParamsString);
  useEffect(() => {
    if (prevSearchParamsRef.current !== searchParamsString) {
      if (lastCreatedSearchId) {
        setLastCreatedSearchId(null);
      }
      prevSearchParamsRef.current = searchParamsString;
    }
  }, [searchParamsString, lastCreatedSearchId, setLastCreatedSearchId]);

  // Fetch address data when addressId is available (via backend API to bypass RLS)
  const [addressData, setAddressData] = useState<NestedAddress | null>(null);
  useEffect(() => {
    if (!addressId) {
      setAddressData(null);
      return;
    }
    let cancelled = false;

    getAPI(`/providers/supabase/addresses/autocomplete/${addressId}`)
      .then((response: { data?: unknown; error?: boolean }) => {
        if (cancelled) return;
        if (!response?.error && response?.data) {
          setAddressData(response.data as NestedAddress);
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [addressId]);

  const handleBuyerDetailsToggle = useCallback(() => {
    toggleSpeculatorDetails(pathname || '');
  }, [pathname, toggleSpeculatorDetails]);

  const handleMapToggle = () => {
    const path = getSearchPath({
      filters,
      showMap: !!map ? false : true,
      tab,
      searchId: searchId || undefined,
    });
    replace(path);
  };

  const renderContent = useMemo(() => {
    if (isLoading) {
      return <IconButton aria-label="Loading" icon={<Spinner size="sm" />} />;
    }

    if (isExactPath(ROUTES.ROOT)) {
      return (
        <ActionButtons
          defaultScrolled={false}
          primaryAction={{
            label: 'Köpa',
            href: ROUTES.SEARCH.HOMES,
          }}
          secondaryAction={{
            label: 'Sälja',
            href: ROUTES.SELL,
          }}
        />
      );
    }

    if (isSearchRoute(pathname) && tab === 'homes') {
      const handleOpenFilters = () =>
        setModals({ ...modals, searchOpen: true });

      const handleEditSearch = () => {
        if (lastCreatedSearchId) {
          setEditSearchId(lastCreatedSearchId);
          setModals({ ...modals, propertySearchOpen: true });
        }
      };

      // Show "Redigera bevakning" if a search was just created, otherwise show "Bevaka" or "Filtrera"
      const primaryAction = lastCreatedSearchId
        ? {
          leftIcon: <Icon as={ICONS.EDIT_SEARCH} />,
          label: 'Redigera bevakning',
          onClick: handleEditSearch,
        }
        : canCreateSearch
          ? {
            leftIcon: isExecutingAction ? (
              <Spinner size="sm" />
            ) : (
              <Icon as={ICONS.HOMEBUYERS_ALERT} />
            ),
            label: isExecutingAction ? 'Bevakar...' : 'Bevaka',
            onClick: () => {
              executeAction({
                action: 'created_search_profile',
                searchFilters: filters || undefined,
              });
            },
          }
          : {
            leftIcon: <VscSettings />,
            label: 'Filter',
            onClick: handleOpenFilters,
          };

      const secondaryAction = map
        ? {
          leftIcon: <FiList />,
          label: 'Lista',
          onClick: () => handleMapToggle(),
          buttonProps: {
            px: '5',
            py: '2',
            borderRadius: '5px',
            backgroundColor: 'white',
            bgGradient: 'linear(to-b, gray.100, gray.100)',
            color: 'black',
            _hover: {
              textDecoration: 'underline',
            },
          },
        }
        : {
          leftIcon: <Icon as={ICONS.MAP_BUTTON} />,
          label: 'Karta',
          onClick: () => handleMapToggle(),
          buttonProps: {
            px: '5',
            py: '2',
            borderRadius: '5px',
            backgroundColor: 'white',
            bgGradient: 'linear(to-b, gray.100, gray.100)',
            color: 'black',
            _hover: {
              textDecoration: 'underline',
            },
          },
        };

      return (
        <ActionButtons
          defaultScrolled={true}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
        />
      );
    }

    if (isSearchRoute(pathname) && tab === 'speculators') {
      // Only privileged users (brokers/admins) can use the Kontakta feature
      const isPrivilegedUser = user && (isBroker(user) || isAdmin(user));
      const hasAddress = label?.includes('address:') ?? false;
      const selectedBuyerCount = buyersSelectionToMatch.length;
      const selectedBuyerIds = buyersSelectionToMatch.map((b) => b.id);
      const effectiveBuyerCount =
        selectedBuyerCount > 0
          ? selectedBuyerCount
          : count.speculatorsUnique || count.speculators || 0;

      // Address display: from label format "address:id:Street, City" (part after last colon)
      const lastColonIndex = label?.lastIndexOf(':') ?? -1;
      const addressFromLabel =
        lastColonIndex > 0 ? label?.substring(lastColonIndex + 1) || '' : '';

      // homeId for modal: from URL only (set when a published home exists for the address)
      const parsedHomeId =
        homeIdFromUrl != null && homeIdFromUrl !== ''
          ? parseInt(homeIdFromUrl, 10)
          : NaN;
      const homeId =
        !isNaN(parsedHomeId) && parsedHomeId > 0 ? parsedHomeId : undefined;

      const street =
        addressData?.street || addressFromLabel.split(',')[0]?.trim() || '';
      const fullAddress = addressData
        ? `${addressData.street}, ${addressData.city || ''}`
        : addressFromLabel;

      // When no published home, default to showing the address (broker is sharing intentionally)
      const show_address = homeId ? undefined : true;

      // Build prefilled title and message using helper functions
      const categorySwedish =
        (addressData?.category &&
          (getCategoryText(addressData.category as CategoryType) ||
            addressData.category)) ||
        'bostad';
      const defaultTitle = hasAddress
        ? getDefaultMessageTitle(categorySwedish, fullAddress, show_address)
        : '';
      const propertyDetails = toPropertyDetails({
        street,
        category: addressData?.category,
        rooms: addressData?.rooms,
        living_space: addressData?.living_space,
        show_address,
      });
      const userDetailsForMessage = {
        firstName: user?.first_name,
        lastName: user?.last_name,
        phone: user?.phone,
        email: user?.email,
      };

      // When no published home exists, include address details (rooms, sqm, fee)
      // directly in the email body so the recipient gets the full picture.
      const addressDetailsSentence =
        !homeId && addressData
          ? getAddressDetailsSentenceForEmail(addressData)
          : null;

      const defaultMessage = hasAddress
        ? addressDetailsSentence
          ? getPersonalMessageBody(propertyDetails, userDetailsForMessage, {
              detailsSentence: addressDetailsSentence,
            })
          : getDefaultMessageBody(propertyDetails, userDetailsForMessage)
        : '';

      // Show "Tipsa" button only for privileged users when an address is selected.
      // Non-privileged users enter the sell flow via the "Sök gatuadress" autocomplete shortcut.
      const showKontaktaButton = hasAddress && isPrivilegedUser;
      const primaryAction = showKontaktaButton
        ? {
          leftIcon: <MdMessage />,
          label: `Tipsa`,
          onClick: sendMessageModal.onOpen,
        }
        : undefined;

      return (
        <>
          <ActionButtons
            defaultScrolled={true}
            primaryAction={primaryAction}
            secondaryAction={
              isPrivilegedUser && count.speculators > 0
                ? {
                  leftIcon: showSpeculatorDetails ? (
                    <ICONS.HIDE_DETAILS />
                  ) : (
                    <ICONS.SHOW_DETAILS />
                  ),
                  label: showSpeculatorDetails
                    ? 'Dölj detaljer'
                    : 'Visa detaljer',
                  onClick: handleBuyerDetailsToggle,
                  buttonProps: {
                    px: '5',
                    py: '2',
                    borderRadius: '5px',
                    backgroundColor: 'white',
                    bgGradient: 'linear(to-b, gray.100, gray.100)',
                    color: 'black',
                    _hover: {
                      textDecoration: 'underline',
                    },
                  },
                }
                : undefined
            }
          />
          {showKontaktaButton && (
            <SendMessageModal
              isOpen={sendMessageModal.isOpen}
              onClose={sendMessageModal.onClose}
              queryParams={searchParams?.toString() || ''}
              buyerCount={effectiveBuyerCount}
              buyerIds={selectedBuyerCount > 0 ? selectedBuyerIds : undefined}
              homeId={homeId}
              defaultTitle={defaultTitle}
              defaultMessage={defaultMessage}
            />
          )}
        </>
      );
    }

    if (isSearchRoute(pathname) && tab === 'brokers') {
      return (
        <ActionButtons
          defaultScrolled={true}
          primaryAction={{
            leftIcon: <MdWork />,
            label: 'Hitta rätt mäklare',
            href: getSellPath(undefined, 'meeting'),
          }}
        />
      );
    }

    if (isSearchRoute(pathname) && tab === 'projects') {
      const primaryAction = map
        ? {
          leftIcon: <FiList />,
          label: 'Lista',
          onClick: () => handleMapToggle(),
        }
        : {
          leftIcon: <Icon as={ICONS.MAP_BUTTON} />,
          label: 'Karta',
          onClick: () => handleMapToggle(),
        };

      return (
        <ActionButtons defaultScrolled={true} primaryAction={primaryAction} />
      );
    }

    return null;
  }, [
    isLoading,
    tab,
    count.projects,
    count.speculators,
    count.brokers,
    user,
    searchId,
    hasFilters,
    handleBuyerDetailsToggle,
    handleMapToggle,
    showSpeculatorDetails,
    map,
    pathname,
    addressData,
  ]);

  return renderContent;
};
